import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Target } from 'lucide-react';

interface MissionCompleteToastProps {
  visible: boolean;
  mission: {
    name: string;
    xpReward: number;
  };
  onDismiss: () => void;
}

export default function MissionCompleteToast({ visible, mission, onDismiss }: MissionCompleteToastProps) {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onDismiss, 4000);
      return () => clearTimeout(timer);
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed top-4 inset-x-4 z-50"
          role="alert"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        >
          <button
            onClick={() => {
              onDismiss();
              navigate('/missions');
            }}
            className="w-full flex items-center gap-3 p-4 rounded-2xl bg-gold/5 border border-gold/20 shadow-lg"
          >
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-gold" />
            </div>
            <div className="flex-1 text-start min-w-0">
              <p className="text-caption font-medium text-muted-foreground">{t('celebrations.missionComplete')}</p>
              <p className="text-small font-bold text-gold truncate">{mission.name}</p>
            </div>
            <span className="text-caption font-bold text-forest bg-forest-wash px-2 py-1 rounded-full whitespace-nowrap">
              +{mission.xpReward} XP
            </span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
